import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { CompareArrows } from "@mui/icons-material";
import StyledAutocomplete from "components/StyledAutocomplete";
import { distinct } from "helpers/coll";
import EditMode from "components/SelectOptions/EditMode";
import { useState } from "react";
import { sanitizeInputString } from "Backend";
import CopyToClipboard from "components/CopyToClipboard";

function sanitizeOptions(options: string[]): string[] {
  return distinct(options.map(sanitizeInputString));
}

interface SelectOptionsProps {
  options: string[];
  setOptions: (options: string[]) => void;
  label?: string;
}

const SelectOptions = ({ options, setOptions, label }: SelectOptionsProps) => {
  const [editMode, setEditMode] = useState(false);

  const setSanitizedOptions = (newOptions: string[]) => {
    setOptions(sanitizeOptions(newOptions));
  };

  if (editMode) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          width: "100%",
          overflow: "hidden",
        }}>
        <EditMode
          setEditMode={setEditMode}
          selectOptions={options}
          setSelectOptions={setSanitizedOptions}
          showAlphebeticalSort
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
      }}>
      <Box sx={{ display: "flex", flexDirection: "column", mt: 1, gap: 1 }}>
        <Tooltip title="Reorder Options">
          <IconButton
            onClick={() => {
              setEditMode(true);
            }}>
            <CompareArrows />
          </IconButton>
        </Tooltip>
        <CopyToClipboard text={options.join(",")} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          pt: 1,
        }}>
        <StyledAutocomplete
          size="small"
          freeSolo
          fullWidth={true}
          multiple
          value={options}
          onChange={(_evt, values) => {
            setSanitizedOptions(values);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              color="secondary"
              id="selectOptions"
              label={label || `Select Options (${options.length})`}
              helperText="Press <Enter> to commit an option or paste comma-separated options."
              size="small"
              onPaste={(event) => {
                event.preventDefault();
                try {
                  const paste = event.clipboardData.getData("text");
                  setSanitizedOptions([...options, ...paste.split(",")]);
                } catch (e) {
                  console.error(e);
                }
              }}
            />
          )}
          options={[]}
        />
      </Box>
    </Box>
  );
};

export default SelectOptions;
